import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../src/components/ui/card';
import { Button } from '../../../src/components/ui/button';
import { Badge } from '../../../src/components/ui/badge';
import { Input } from '../../../src/components/ui/input';
import { BookOpen, Search, ArrowRight, HelpCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import {
  GOVERNANCE_GLOSSARY_TERMS,
  GLOSSARY_CATEGORIES,
} from '../../../src/data/governance/glossary';

const glossaryTerms = GOVERNANCE_GLOSSARY_TERMS;

export default function MontanaGlossaryPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTerm, setSelectedTerm] = useState<string>('');
  const [copiedPage, setCopiedPage] = useState(false);
  const [copiedTerm, setCopiedTerm] = useState(false);

  // Canonical production base for customer-facing links
  const CANONICAL_BASE =
    (import.meta as any)?.env?.VITE_DEMO_CANONICAL_BASE || 'https://montana.toolboxlogic.com';
  const PAGE_PATH = '/governance/glossary';

  // Navigate back function for Montana demo
  const goBack = () => {
    window.location.href = '/governance';
  };

  // Initialize selected term from URL hash and keep URL in sync with selection
  useEffect(() => {
    // Enforce HTTPS when served under toolboxlogic.com domains
    try {
      const host = window.location.hostname;
      const isToolboxlogic = host.endsWith('toolboxlogic.com');
      const isHttp = window.location.protocol === 'http:';
      if (isToolboxlogic && isHttp) {
        const to = `https://${host}${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.replace(to);
        return; // bail out after redirect
      }
    } catch {}

    // On mount or hash change, select the term if present
    const applyHash = () => {
      const hash = window.location.hash.replace('#', '').trim();
      if (hash && glossaryTerms.some((t) => t.id === hash)) {
        setSelectedTerm(hash);
      } else if (!hash) {
        setSelectedTerm('');
      }
    };

    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, []);

  useEffect(() => {
    // Update the URL hash when the selected term changes
    const newHash = selectedTerm ? `#${selectedTerm}` : '';
    if (newHash !== window.location.hash) {
      // Use replaceState to avoid polluting history while browsing terms
      const url = `${window.location.pathname}${newHash}${window.location.search}`;
      window.history.replaceState(null, '', url);
    }
  }, [selectedTerm]);

  const filteredTerms = useMemo(() => {
    let terms = glossaryTerms;

    // Filter by category
    if (selectedCategory !== 'all') {
      terms = terms.filter((term) => term.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      terms = terms.filter(
        (term) =>
          term.term.toLowerCase().includes(query) ||
          term.shortDefinition.toLowerCase().includes(query) ||
          term.fullDefinition.toLowerCase().includes(query),
      );
    }

    return terms;
  }, [searchQuery, selectedCategory]);

  const categories = GLOSSARY_CATEGORIES.map((c) => ({
    ...c,
    count:
      c.id === 'all'
        ? glossaryTerms.length
        : glossaryTerms.filter((t) => t.category === c.id).length,
  }));

  const selectedTermData = selectedTerm ? glossaryTerms.find((t) => t.id === selectedTerm) : null;
  const canonicalUrl = `${CANONICAL_BASE}${PAGE_PATH}`;
  const canonicalTermUrl = selectedTermData
    ? `${CANONICAL_BASE}${PAGE_PATH}#${selectedTermData.id}`
    : canonicalUrl;

  const copyToClipboard = async (text: string, kind: 'page' | 'term') => {
    try {
      await navigator.clipboard.writeText(text);
      if (kind === 'page') {
        setCopiedPage(true);
        setTimeout(() => setCopiedPage(false), 1500);
      } else {
        setCopiedTerm(true);
        setTimeout(() => setCopiedTerm(false), 1500);
      }
    } catch (e) {
      // no-op if clipboard not available
    }
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-blue-50 to-purple-50">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Montana Governance Glossary</h1>
              <p className="text-muted-foreground">
                Plain-English explanations of community governance terms for 406-member Montana
                communities
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={goBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Governance
          </Button>
        </div>

        {/* Quick Stats */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-blue-600 mb-2">{glossaryTerms.length}</div>
                <div className="text-sm text-muted-foreground">Terms Explained</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-green-600 mb-2">4</div>
                <div className="text-sm text-muted-foreground">Categories Covered</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600 mb-2">100%</div>
                <div className="text-sm text-muted-foreground">Plain English</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Search and Categories */}
          <div className="lg:col-span-4">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Find Terms
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search Input */}
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search terms..."
                    value={searchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSearchQuery(e.target.value)
                    }
                    className="pl-9"
                  />
                </div>

                {/* Category Filters */}
                <div className="space-y-2">
                  <div className="text-sm font-medium">Categories</div>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full text-left p-2 rounded-lg text-sm transition-colors ${
                        selectedCategory === category.id
                          ? 'bg-blue-100 text-blue-900 border border-blue-200'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{category.label}</span>
                        <Badge variant="secondary" className="text-xs">
                          {category.count}
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Share Link */}
                <div className="border-t pt-4">
                  <div className="text-sm font-medium mb-2">Share This Page</div>
                  <div className="text-xs text-muted-foreground mb-2">
                    Direct link to Montana glossary:
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-gray-50 rounded text-xs font-mono break-all flex-1">
                      {canonicalUrl}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(canonicalUrl, 'page')}
                    >
                      {copiedPage ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Terms List and Detail */}
          <div className="lg:col-span-8">
            {selectedTermData ? (
              /* Term Detail View */
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <selectedTermData.icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl">{selectedTermData.term}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className="capitalize">
                            {selectedTermData.category.replace('-', ' ')}
                          </Badge>
                          {selectedTermData.traditionalEquivalent && (
                            <Badge variant="outline" className="text-xs">
                              vs: {selectedTermData.traditionalEquivalent.split('(')[0].trim()}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedTerm('')}>
                      <ArrowRight className="w-4 h-4 rotate-180" />
                      Back to List
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Quick Definition */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-sm font-medium text-blue-800 mb-2">Quick Definition</div>
                    <div className="text-blue-900">{selectedTermData.shortDefinition}</div>
                  </div>

                  {/* Full Explanation */}
                  <div>
                    <div className="text-lg font-semibold mb-3">Detailed Explanation</div>
                    <div className="text-muted-foreground leading-relaxed">
                      {selectedTermData.fullDefinition}
                    </div>
                  </div>

                  {/* Examples */}
                  <div>
                    <div className="text-lg font-semibold mb-3">
                      Real Examples in Montana Communities
                    </div>
                    <div className="space-y-2">
                      {selectedTermData.examples.map((example, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg"
                        >
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                          <div className="text-sm text-green-800">{example}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Traditional Equivalent */}
                  {selectedTermData.traditionalEquivalent && (
                    <div>
                      <div className="text-lg font-semibold mb-3">Compared to Traditional HOAs</div>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="text-amber-900">
                          {selectedTermData.traditionalEquivalent}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Related Terms */}
                  {selectedTermData.relatedTerms.length > 0 && (
                    <div>
                      <div className="text-lg font-semibold mb-3">Related Terms</div>
                      <div className="flex flex-wrap gap-2">
                        {selectedTermData.relatedTerms.map((termId) => {
                          const relatedTerm = glossaryTerms.find((t) => t.id === termId);
                          return relatedTerm ? (
                            <Button
                              key={termId}
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedTerm(termId)}
                              className="text-sm"
                            >
                              {relatedTerm.term}
                              <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* Share Link for Specific Term */}
                  <div className="border-t pt-4">
                    <div className="text-sm font-medium mb-2">Share Link to This Term</div>
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-gray-50 rounded text-xs font-mono break-all flex-1">
                        {canonicalTermUrl}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(canonicalTermUrl, 'term')}
                      >
                        {copiedTerm ? 'Copied' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Terms List View */
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">
                      {selectedCategory === 'all'
                        ? 'All Terms'
                        : categories.find((c) => c.id === selectedCategory)?.label}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {filteredTerms.length} term{filteredTerms.length !== 1 ? 's' : ''} found
                    </p>
                  </div>
                </div>

                <div className="grid gap-4">
                  {filteredTerms.map((term) => (
                    <Card
                      key={term.id}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => setSelectedTerm(term.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                              <term.icon className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{term.term}</h3>
                                <Badge className="text-xs capitalize">
                                  {term.category.replace('-', ' ')}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {term.shortDefinition}
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground ml-2 shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {filteredTerms.length === 0 && (
                  <Card>
                    <CardContent className="text-center py-12">
                      <HelpCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-semibold mb-2">No terms found</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Try adjusting your search or selecting a different category.
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedCategory('all');
                        }}
                      >
                        Clear Filters
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Call to Action */}
        <Card className="mt-12 bg-linear-to-r from-blue-50 to-purple-50 border-blue-200">
          <CardContent className="text-center p-8">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Experience Montana Digital Governance?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              See how these governance concepts work together to transform your 406-member Montana
              community's decision-making process.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700" onClick={goBack}>
                Back to Demo
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline">
                Schedule Montana Demo Call
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
